import React, { useState } from "react";
import "./Avatar.scss";

type AvatarSize = "xs" | "sm" | "md" | "lg";

const FALLBACK_COLORS = [
  "#4A90D9", // blue
  "#E67E22", // orange
  "#27AE60", // green
  "#8E44AD", // purple
  "#E74C3C", // red
  "#16A085", // teal
  "#2C3E50", // slate
  "#E91E63", // pink
];

function hashString(str: string): number {
  const s = str || "";
  let hash = 5381;
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 33) ^ s.charCodeAt(i);
  }
  return Math.abs(hash);
}

interface AvatarProps {
  size?: AvatarSize;
  className?: string;
  children: React.ReactNode;
}

export const Avatar: React.FC<AvatarProps> = ({ size = "md", className = "", children }) => {
  return <div className={`Avatar Avatar--${size} ${className}`}>{children}</div>;
};

interface AvatarImageProps {
  src: string;
  alt: string;
  onError?: () => void;
}

export const AvatarImage: React.FC<AvatarImageProps> = ({ src, alt, onError }) => {
  const [hasError, setHasError] = useState(false);
  const [lastSrc, setLastSrc] = useState(src);

  // Reset error state when src changes (e.g. after a refresh)
  if (src !== lastSrc) {
    setLastSrc(src);
    setHasError(false);
  }

  if (hasError || !src) {
    return null;
  }

  return (
    <img
      className="Avatar__Image"
      src={src}
      alt={alt}
      onError={() => {
        setHasError(true);
        onError?.();
      }}
    />
  );
};

interface AvatarFallbackProps {
  initials: string;
  colorSeed: string;
}

export const AvatarFallback: React.FC<AvatarFallbackProps> = ({ initials, colorSeed }) => {
  const colorIndex = hashString(colorSeed) % FALLBACK_COLORS.length;
  const backgroundColor = FALLBACK_COLORS[colorIndex];

  return (
    <div className="Avatar__Fallback" style={{ backgroundColor }}>
      {initials || "?"}
    </div>
  );
};
